// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
import { ProofIncomeMethodsEnumeration } from 'containers/proof-income/enumerations/proof-income-methods.enumeration';
import { ProofIncomeStatesEnumeration } from 'containers/proof-income/enumerations/proof-income-states.enumeration';
// Resolvers
import {
  completeOpenbankingResolver,
  invalidateOpenbankingResolver,
} from '../resolvers/proof-income-method.resolver';
import { backPreviousStateResolver } from 'resolvers/back-state.resolver';
// Types
import { GetStateSuccessType } from 'types/get-state-success.type';
import {
  ProofIncomeMethodsBuilderHandlersType,
  ProofIncomeMethodsCompleteBuilderHandlersType,
  ProofIncomeMethodsIframeBuilderHandlersType,
  ProofIncomeMethodsTGSSIframeBuilderHandlersType,
  ProofIncomeMethodsInvalidateBuilderHandlersType,
  ProofIncomeMethodsMethodBuilderHandlersType,
  ProofIncomeMethodsMethodsHandlersType,
  ProofIncomePaddingBuilderHandlersType,
  ProofIncomeUpdatHeaderBackClickHandlerType,
} from './types/proof-income-method.handlers.type';
// Utils
import { eventIsNotFromTink, isErrorType, isReportsType } from 'utils/identification.utils';
import {
  eventIsNotFromTgss,
  isTGSSSelectedMethod,
  isWidgetHeightEvent,
} from './utils/proof-income-method.handlers.utils';

const manageReceivedDataFromWSEventHandler = ({
  state,
  setCurrentState,
  setError,
}: ProofIncomeMethodsMethodBuilderHandlersType): void => {
  switch (state) {
    case AppStatesEnumeration.CHECKOUT_LOADING:
      setCurrentState(AppStatesEnumeration.LOADING);
      break;
    case AppStatesEnumeration.CHECKOUT_WORKLIFE:
      break;
    case AppStatesEnumeration.CHECKOUT_IDENTIFICATION:
      setCurrentState(AppStatesEnumeration.CHECKOUT_IDENTIFICATION);
    case AppStatesEnumeration.CHECKOUT_SIGNING:
      setCurrentState(AppStatesEnumeration.CHECKOUT_SIGNING);
    case AppStatesEnumeration.CHECKOUT_INCOME:
    default:
      setError(AppErrorEnumeration.PROOF_OF_INCOME_WS_REJECTED);
  }
};

const completeOpenbankingHandler = async ({
  proofIncomeIdentificationId,
  reportIds,
  setCurrentState,
  setError,
}: ProofIncomeMethodsCompleteBuilderHandlersType): Promise<void> => {
  const [error] = await completeOpenbankingResolver({
    proofIncomeIdentificationId,
    reportIds,
  });
  if (error) {
    setError(AppErrorEnumeration.COMPLETE_OPENBANKING_FAILED);
  } else {
    setCurrentState(AppStatesEnumeration.LOADING);
  }
};

const invalidateOpenbankingHandler = async ({
  proofIncomeIdentificationId,
  setError,
}: ProofIncomeMethodsInvalidateBuilderHandlersType): Promise<void> => {
  const [,] = await invalidateOpenbankingResolver(proofIncomeIdentificationId);
  setError(AppErrorEnumeration.OPENBANKING_INVALID_TINK_ERROR);
};

const manageReceiveDataFromtTinkIframeEventHandler = async ({
  event,
  proofIncomeIdentificationId,
  setError,
  setCurrentState,
}: ProofIncomeMethodsIframeBuilderHandlersType): Promise<void> => {
  if (eventIsNotFromTink(event.origin)) {
    return;
  }
  const { type, data, error } = JSON.parse(event.data);
  if (isReportsType(type)) {
    const { account_verification_report_id, transaction_report_id } = data;
    await completeOpenbankingHandler({
      proofIncomeIdentificationId,
      setCurrentState,
      setError,
      reportIds: { account_verification_report_id, transaction_report_id },
    });
  } else if (isErrorType(type) || error) {
    await invalidateOpenbankingHandler({ proofIncomeIdentificationId, setError });
  }
};

const manageReceiveDataFromtTGSSIframeEventHandler = ({
  event,
  setHeight,
}: ProofIncomeMethodsTGSSIframeBuilderHandlersType): void => {
  if (eventIsNotFromTgss(event.origin)) {
    return;
  }

  const { data, type } = event.data;

  if (isWidgetHeightEvent(type)) {
    setHeight(data);
  }
};

const toggleEntrypointCenterPaddingEventHandler = ({
  setPaddingContent,
  selectedMethod,
  toggle,
}: ProofIncomePaddingBuilderHandlersType): void => {
  isTGSSSelectedMethod(selectedMethod) && setPaddingContent(toggle);
};

const manageIframeEventsListenerHandler = ({
  event,
  proofIncomeIdentificationId,
  setError,
  setCurrentState,
  selectedMethod,
  setHeight,
}): void | Promise<void> =>
  ({
    [ProofIncomeMethodsEnumeration.OPENBANKING]: () =>
      manageReceiveDataFromtTinkIframeEventHandler({
        event,
        proofIncomeIdentificationId,
        setError,
        setCurrentState,
      }),
    [ProofIncomeMethodsEnumeration.TGSS]: () =>
      manageReceiveDataFromtTGSSIframeEventHandler({ event, setHeight }),
  }[selectedMethod as ProofIncomeMethodsEnumeration]());

const updateProofIncomeHeaderBackClickHandler = async ({
  setError,
  setProofIncomeState,
}: ProofIncomeUpdatHeaderBackClickHandlerType): Promise<void> => {
  const [success] = await backPreviousStateResolver();
  if (success) {
    setProofIncomeState(ProofIncomeStatesEnumeration.LANDING);
  } else {
    setError(AppErrorEnumeration.BACK_STATE_PROOF_OF_INCOME_FAILED);
  }
};

const onPOIPollingErrorHandler = ({
  setError,
}: Pick<ProofIncomeMethodsBuilderHandlersType, 'setError'>): void => {
  setError(AppErrorEnumeration.PROOF_OF_INCOME_WS_REJECTED);
};

const ProofIncomeMethodHandlers = ({
  selectedMethod,
  setPaddingContent,
  setProofIncomeState,
  ...props
}: ProofIncomeMethodsBuilderHandlersType): ProofIncomeMethodsMethodsHandlersType => ({
  handleManageReceivedDataFromWSEvent: (wsProps: GetStateSuccessType) =>
    manageReceivedDataFromWSEventHandler({ ...props, ...wsProps }),
  handleManageReceiveDataFromIframeEvent: (event: MessageEvent) =>
    manageIframeEventsListenerHandler({ ...props, selectedMethod, event }),
  handleToggleEntrypointCenterPaddingEvent: (toggle: boolean) =>
    toggleEntrypointCenterPaddingEventHandler({ setPaddingContent, selectedMethod, toggle }),
  handleUpdateProofIncomeHeaderBackClickHandler: () =>
    updateProofIncomeHeaderBackClickHandler({ ...props, setProofIncomeState }),
  handleOnPOIPollingError: () => onPOIPollingErrorHandler(props),
});

export { ProofIncomeMethodHandlers };
